import {
  clamp,
  concat,
  entries,
  filter,
  groupBy,
  isArray,
  isDate,
  isEmpty,
  isFunction,
  isNullish,
  isNumber,
  isObjectType,
  isPlainObject,
  isString,
  isTruthy,
  map,
  mapValues,
  pipe,
  prop,
  sortBy,
  stringToPath,
  toLowerCase,
  unique,
  values,
} from 'remeda';
import * as R from 'remeda';
import { phone } from 'phone';
import {
  DateTime,
  DateTimeUnit,
  Duration,
  DurationLike,
  Interval,
  DateTimeFormatOptions,
} from 'luxon';
import objectHash, { NormalOption } from 'object-hash';
import {
  BackendOpportunityFilters,
  DateFilter,
  DateFilterPresetOption,
  DurationLikeObject,
  OpportunityDateFieldPath,
  OpportunityDateFieldTarget,
  OpportunityDateFilter,
  OpportunityFilters,
  TrackedEntity,
  TrackedEntityType,
} from './schemas/common.js';
import {
  AtsView,
  EntityTrailItem,
  IChatMessage,
  NavigationTrailItem,
} from './schemas/models/llmChatSchema.js';
import { BrowsingContext } from './schemas/askToroChatApiSchemas.js';

export { doesUserHaveAccess } from './permissions.js';

export async function delay(delayTime: DurationLike): Promise<void> {
  const delayMs = Duration.fromDurationLike(delayTime).toMillis();
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

export * from './schemas/utils.js';

function _cleanupPhone(number: string, includePrefix = true) {
  const { isValid, phoneNumber, countryCode } = phone(number);
  if (!isValid) {
    return '';
  }

  if (includePrefix) {
    return phoneNumber;
  } else {
    return phoneNumber.replace(countryCode, '');
  }
}

/**
 * Normalizes a phone number to the E164 format including countryCode (e.g. +1)
 */
export const cleanupPhone = (phoneNumber: string) => _cleanupPhone(phoneNumber, true);

/**
 * Normalizes a phone number and removes the countryCode
 */
export const cleanupPhoneNoPrefix = (phoneNumber: string) => _cleanupPhone(phoneNumber, false);

export function cleanupEmail(email: string) {
  return toLowerCase(email || '').trim();
}

type Limits = {
  readonly min?: number;
  readonly max?: number;
};
export function clampWithDefault(
  val: number | undefined | null,
  defaultValue: number,
  limits: Limits = {}
) {
  const { min = 0, max = defaultValue } = limits;
  return clamp(val ?? defaultValue, { min, max });
}

/**
 * Takes 2 numbers and returns a percentage.
 * @param count - numerator
 * @param total - denominator
 * @param precision - decimal places to include. Defaults to 1
 */
export function toPercentage(count?: number, total?: number, precision = 0) {
  if (!count) {
    return 0;
  } else if (!total) {
    return 100;
  }
  const factor = Math.pow(10, clamp(precision, { min: 0, max: 10 }));
  return Math.round((count / total) * 100 * factor) / factor;
}

interface NormalizeStringArrayOptions {
  split?: string;
  formatter?: (val: string) => string;
}
// Helper to map over an array of unknown inputs and filter/flatten them to formatted strings
export function normalizeStringArray(
  values: unknown[],
  options: NormalizeStringArrayOptions = {}
): string[] {
  const { formatter = (val: string) => val, split } = options;

  return R.pipe(
    values,
    // Flatten nested arrays if we get any
    R.flat(),
    // Filter to truthy strings
    R.filter(R.isString),
    // Split on the given character then re-flatten if needed
    R.map((val) => (split ? val.split(split) : val)),
    R.flat(),
    R.map((str) => str.trim()),
    R.filter(R.isTruthy),
    // Run each value through the formatter, if given
    R.map(formatter),
    // Filter again after formatting
    R.filter(R.isTruthy),
    // Dedupe results
    R.unique()
  );
}

/** @deprecated */
export function depopulate(document: any): any {
  if (isArray(document)) {
    return map(document, depopulate);
  }
  if (!isFunction(document?.toObject)) {
    return document;
  }
  return document.toObject({ depopulate: true });
}

/** @deprecated */
export function depopulateValues(object: Record<string, any>) {
  return mapValues(object, depopulate);
}

export function toDateTimeOptional(val: unknown): DateTime<true> | null {
  if (!val) {
    return null;
  }
  try {
    return toDateTime(val, true);
  } catch (err) {
    return null;
  }
}

const tenYearsAgo = DateTime.now().minus({ years: 10 }).toMillis();
const tenYearsFromNow = DateTime.now().plus({ years: 10 }).toMillis();
export function isNumberProbablyADate(val: unknown) {
  return Boolean(isNumber(val) && val > tenYearsAgo && val < tenYearsFromNow);
}

export function toDateTime(val: unknown, strict: true): DateTime<true>;
export function toDateTime(val: unknown, strict?: boolean): DateTime<boolean>;
export function toDateTime(val: unknown, strict?: boolean): DateTime<boolean> {
  const dateTime = getDateTime(val);
  if (strict && !dateTime.isValid) {
    throw new Error(`Invalid DateTime: ${dateTime.invalidExplanation || dateTime.invalidReason}`);
  }
  return dateTime;
}

export const toDateTimeStrict = (val: unknown): DateTime<true> => toDateTime(val, true);

function getDateTime(val: unknown) {
  if (DateTime.isDateTime(val)) {
    return val;
  } else if (isDate(val)) {
    return DateTime.fromJSDate(val);
  } else if (isNumber(val)) {
    return DateTime.fromMillis(val);
  }
  if (isString(val)) {
    return DateTime.fromISO(val);
  }
  if (isPlainObject(val)) {
    return DateTime.fromObject(val);
  }
  return DateTime.invalid(`Type '${typeof val}' cannot be converted to a dateTime`);
}

/**
 * @deprecated
 *   Use toDateString() with a locale format if displaying to users,
 *   DateTime.toISO() for a full dateTime string,
 *   DateTime.toISODate() for a date string,
 *   or DateTime.toFormat() as a last resort if a more specific backend format is needed.
 */
export function toDateStringRaw(format: string, val: unknown): string {
  if (!val) {
    return 'N/A';
  }
  try {
    const dateTime = toDateTime(val);
    return dateTime.toFormat(format);
  } catch (err) {
    return '(invalid)';
  }
}

/**
 * Converts a date-like value to a formatted string using the specified format.
 *
 * @param format - Output format for the date string, e.g. `DateTime.DATE_SHORT`
 * @param val - The date value to format.
 * @param [fallback='N/A'] - String to display if input is undefined.
 */
export function toDateString(
  format: DateTimeFormatOptions,
  val: unknown,
  fallback: string = 'N/A'
): string {
  if (!val) {
    return fallback;
  }
  try {
    const dateTime = toDateTime(val, true);
    return dateTime.toLocaleString(format);
  } catch (err) {
    return '(invalid)';
  }
}

// Parse a date from a string using one or more formats
export function parseDate(input: string, pattern: string): DateTime<true> | null;
export function parseDate(input: string, patternList: readonly string[]): DateTime<true> | null;
export function parseDate(
  input: string,
  pattern: string | readonly string[]
): DateTime<true> | null {
  const patternList: readonly string[] = isArray(pattern) ? pattern : [pattern];
  // Try each format until we find one that works
  for (const format of patternList) {
    const dateTime = DateTime.fromFormat(input, format);
    if (dateTime.isValid) {
      return dateTime;
    }
  }
  return null;
}

// Weekday enum used by Luxon
export const WEEKDAYS = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;

/**
 * Adds or subtracts a given number of workdays to a start date, skipping weekends and holidays
 * @param startDate
 * @param days
 */
export function addWorkDays(startDate: DateTime<true>, days: number): DateTime {
  startDate = toDateTime(startDate);
  // TODO: account for holidays
  const isPositive = Boolean(days >= 0);
  if (!isPositive) {
    days = Math.abs(days);
  }
  const numWorkWeeks = Math.floor(days / 5);
  const remainder = days % 5;

  let result: DateTime = startDate;
  if (isPositive) {
    // If the last week segment contains/enters a weekend, add 2 more days
    const lastWeekStart = startDate.plus({ weeks: numWorkWeeks });
    result = lastWeekStart.plus({ days: remainder });
    if (result.weekday < lastWeekStart.weekday || result.weekday >= WEEKDAYS.SATURDAY) {
      result = result.plus({ days: 2 });
    }
  } else {
    // If the last week segment contains/enters a weekend, subtract 2 more days
    const lastWeekEnd = startDate.minus({ weeks: numWorkWeeks });
    result = lastWeekEnd.minus({ days: remainder });
    if (result.weekday > lastWeekEnd.weekday || lastWeekEnd.weekday >= WEEKDAYS.SATURDAY) {
      result = result.minus({ days: 2 });
    }
  }

  return result;
}

export function subtractWorkDays(startDate: DateTime<true>, days: number): DateTime {
  return addWorkDays(startDate, -days);
}

export function getDateRangeForPreset(preset: DateFilterPresetOption) {
  const today = DateTime.now().startOf('day');

  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today.endOf('day') };
    case 'yesterday': {
      const startDate = today.minus({ days: 1 });
      return { startDate, endDate: startDate.endOf('day') };
    }
    case 'last7days':
      return { startDate: today.minus({ days: 6 }), endDate: today.endOf('day') };
    case 'last30days':
      return { startDate: today.minus({ days: 29 }), endDate: today.endOf('day') };
    case 'last90days':
      return { startDate: today.minus({ days: 89 }), endDate: today.endOf('day') };
    default:
      throw exhaustiveSwitchCheck(preset);
  }
}

type DateFilterRange =
  | { startDate: DateTime<true>; endDate?: DateTime<true> }
  | { startDate?: DateTime<true>; endDate: DateTime<true> };
// Returns the startDate and endDate of a given dateFilter. Returns null for invalid/incomplete filters
export function getDateFilterRange(dateFilter?: DateFilter | null): DateFilterRange | null {
  if (!dateFilter) return null;

  const { type } = dateFilter;
  switch (type) {
    case 'preset':
      return getDateRangeForPreset(dateFilter.preset);
    case 'range': {
      const { startDate, endDate } = dateFilter;
      return startDate?.isValid && endDate?.isValid ?
          {
            startDate: startDate.startOf('day'),
            endDate: endDate.endOf('day'),
          }
        : null;
    }
    case 'before': {
      const { date } = dateFilter;
      return date?.isValid ?
          {
            endDate: date.endOf('day'),
          }
        : null;
    }
    case 'after': {
      const { date } = dateFilter;
      return date?.isValid ?
          {
            startDate: date.startOf('day'),
          }
        : null;
    }
    case 'exact': {
      const { date } = dateFilter;
      return date?.isValid ?
          {
            startDate: date.startOf('day'),
            endDate: date.endOf('day'),
          }
        : null;
    }
    default:
      throw exhaustiveSwitchCheck(type);
  }
}

export function toBoolean(val: unknown): boolean {
  return Boolean(val && !(isString(val) && val.toLowerCase().trim() === 'false'));
}

export const doNothing = R.doNothing();
export const noop = doNothing;

/**
 * Returns true for null, undefined, plus empty objects/arrays/maps
 */
export function isEmptyIsh(val: unknown) {
  return isNullish(val) || (isObjectType(val) && isEmpty(val));
}

export function hasValueRecursive(val: unknown): boolean {
  if (isObjectType(val) || isArray(val)) {
    // If val is an object/array, check if it has at least one truthy value in it (recursively following the same logic)
    return values(val as Record<string, unknown> | unknown[]).some((item) =>
      hasValueRecursive(item)
    );
  } else {
    // Return true for any other truthy value
    return isTruthy(val);
  }
}

export function isEmptyIshRecursive(val: unknown) {
  return !hasValueRecursive(val);
}

/**
 * Returns true if the list contains the value, case-insensitive
 * @param list
 * @param value
 */
export function includesCaseInsensitive(list: readonly string[], value: string | unknown): boolean {
  return Boolean(findCaseInsensitive(list, value));
}

export function findCaseInsensitive<T extends readonly string[]>(
  list: T,
  value: string | unknown
): T[number] | undefined {
  if (!isString(value)) {
    return;
  }
  return list.find((item) => item.toLowerCase() === value.toLowerCase());
}

/** Normalize a scalar to a case-insensitive comparison key (trimmed, lowercased); non-scalars and empty values → null. */
export function normalizeComparisonKey(val: unknown): string | null {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'string') return val.trim().toLowerCase();
  if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
    return String(val);
  }
  return null;
}

// Helper to recursively get a path from an object/array
export function getPathRecursive(
  obj: unknown,
  path: string | readonly (string | number)[]
): unknown {
  path = isArray(path) ? path : stringToPath(path);
  return _getPathRecursive(obj, path);

  function _getPathRecursive(obj: unknown, path: readonly (string | number)[]): unknown {
    const [key, ...remainingSubPath] = path;
    if (!obj) {
      return undefined;
    }
    let value = _getValue(obj, key);

    // If we reached the end of the path, return whatever we have
    if (isEmpty(remainingSubPath)) {
      return value;
    }
    // If the value is an array, map over the values to get the remaining subPath
    if (isArray(value)) {
      return value.map((item) => _getPathRecursive(item, remainingSubPath));
    } else if (isObjectType(value)) {
      return _getPathRecursive(value, remainingSubPath);
    } else {
      return undefined;
    }
  }

  // Gets the value at `key` from `obj`. If it's an array and the key is not a number, maps over the array and returns all values
  function _getValue(obj: unknown, key: string | number): unknown {
    if (isArray(obj)) {
      if (isNumber(key)) {
        return obj[key];
      } else {
        return obj.map((val) => _getValue(val, key));
      }
    }
    if (isObjectType(obj) && has(obj, key)) {
      return obj[key];
    }
    return undefined;
  }
}

/**
 * Recusively checks an error and its cause chain for a given condition.
 * Data-first version.
 */
export function recursiveErrorCheck(
  error: unknown,
  condition: (error: unknown) => boolean | null | undefined
): boolean;
/**
 * Recusively checks an error and its cause chain for a given condition.
 * Data-last version.
 */
export function recursiveErrorCheck(
  condition: (error: unknown) => boolean | null | undefined
): (error: unknown) => boolean;
export function recursiveErrorCheck(...args: unknown[]) {
  return R.purry(_recursiveErrorCheck, args);
}
function _recursiveErrorCheck(
  error: unknown,
  condition: (error: unknown) => boolean | null | undefined
): boolean {
  // Stop if recursion gets too deep to prevent hanging on circular references
  for (let level = 0; level < 10; level++) {
    if (!error) return false;
    if (condition(error)) return true;
    if (!R.isError(error)) return false;
    error = error.cause;
  }
  return false;
}

/**
 * Increments the value of a specified key in a given Map.
 * If the key does not exist in the Map, it will be added with a value of 1.
 *
 * @param {Map<string, number>} map - The Map to modify.
 * @param {string} key - The key to increment.
 */
export function incrementKey(map: Map<string, number>, key: string) {
  if (!key || !isString(key)) {
    return;
  }
  const currentValue = map.get(key) || 0;
  map.set(key, currentValue + 1);
}

type MapLike<T> = Map<string, T> | Record<string, T>;

/**
 * Converts a Map or object into an array of key-value pairs.
 * @template T
 * @param input - The Map or object to convert.
 *
 * @return Array of key-value pairs.
 */
export function toArray<T>(input: MapLike<T>): { key: string; value: T }[] {
  const iterableMap = input instanceof Map ? input : entries(input);
  return Array.from(iterableMap, ([key, value]) => ({ key, value }));
}

/**
 * Asserts that an array is non-empty and casts its type to reflect that.
 * Mainly intended for passing dynamically calculated arrays into zod schemas
 */
export function toNonEmpty<T>(list: readonly T[]): readonly [T, ...T[]] {
  if (list.length === 0) {
    throw new Error('List is empty');
  }
  return list as [T, ...T[]];
}

/**
 * _.fromPairs(), except that duplicate keys are returned as an array of values instead of the last value
 */
export function fromPairsWithDuplicates<T>(pairs: readonly [string, T][]): {
  [key: string]: T | T[];
} {
  const grouped = groupBy(pairs, ([key]) => key);
  return mapValues(grouped, (group) => {
    const values = map(group, ([_key, val]) => val);
    if (values.length === 1) {
      return values[0];
    } else {
      return values;
    }
  });
}

/**
 * Converts a Map or object to an array of objects containing the name and count, sorted in descending order.
 *
 * @param input - The Map or object to convert.
 * @return The converted array of objects.
 */
export function toCounts(input: MapLike<number>): { name: string; count: number }[] {
  return pipe(
    input,
    toArray,
    map(({ key, value }) => ({ name: key, count: value })),
    sortBy([prop('count'), 'desc'])
  );
}

/*
 * True when both arrays hold the same set of values, ignoring order and duplicates.
 * Equivalent to an empty Set.symmetricDifference, whose types need an ES2024 lib this
 * package does not load.
 */
export const hasSameElements = <T>(a?: Array<T> | null, b?: Array<T> | null): boolean => {
  const setA = new Set(a ?? []);
  const setB = new Set(b ?? []);
  return setA.size === setB.size && [...setA].every((value) => setB.has(value));
};

// Shared so the streaming path, the confirmation path and the UI can't disagree about what succeeded
export function toolResultStatus(result: unknown): 'completed' | 'failed' {
  return isObjectType(result) && 'error' in result && !isNullish(result.error) ?
      'failed'
    : 'completed';
}

/*
 * Whether a message is part of the conversation's record. Complete messages are, and so is a question the
 * user stopped, replied to or not. Shared so the panel and the model's history agree on what a turn was.
 */
export function isHistoryMessage(message: Pick<IChatMessage, 'role' | 'status'>): boolean {
  return (
    message.status === 'complete' || (message.role === 'user' && message.status === 'terminated')
  );
}

// Type guard to see if a key is in an object
export function has<T extends object>(obj: T, key: unknown): key is keyof T {
  if (isString(key)) {
    return key in obj;
  } else {
    return false;
  }
}

const hashOptions: NormalOption = {
  encoding: 'hex',
  algorithm: 'sha1',
  unorderedObjects: true,
};
export const hashObject = (data: any) => {
  return objectHash(data, hashOptions);
};

const mockIdBase = R.randomInteger(0, 0xffff).toString(16).padStart(4, '0');
let mockIdIterator = 0;
// Creates a valid and unique, but clearly fake ObjectId string (starting with several zeroes)
export function getFakeObjectIdString() {
  const parts = [
    '0000',
    DateTime.now().toUnixInteger().toString(16),
    mockIdBase,
    mockIdIterator.toString(16),
  ];
  mockIdIterator++;
  return parts.join('').padStart(24, '0').toLowerCase();
}

export interface IntervalParams {
  startDate?: DateTime;
  endDate?: DateTime;
  duration?: DurationLike;
}

export const getInterval = (
  params: IntervalParams,
  accuracyLevel: DateTimeUnit = 'minute'
): Interval<true> => {
  const _getInterval = () => {
    let { duration, startDate, endDate } = params;
    if (duration && startDate && endDate) {
      const interval = Interval.fromDateTimes(
        startDate.startOf(accuracyLevel),
        endDate.startOf(accuracyLevel)
      );
      if (!interval.toDuration().rescale().equals(Duration.fromDurationLike(duration).rescale())) {
        throw new Error('Invalid Interval params: length does not match');
      }
      return interval;
    } else if (duration && !startDate) {
      endDate ||= DateTime.now().startOf(accuracyLevel);
      return Interval.before(endDate, duration);
    } else if (duration && startDate) {
      return Interval.after(startDate.startOf(accuracyLevel), duration);
    } else {
      throw new Error('Invalid Interval params: `startDate` or `duration` is required');
    }
  };

  const interval = _getInterval();
  if (!interval.isValid) {
    throw new Error(`Invalid interval: ${interval.invalidReason}: ${interval.invalidExplanation}`);
  }
  return interval;
};

export const formatDuration = (durationLike: DurationLikeObject) => {
  return Duration.fromObject(durationLike).toHuman({
    unitDisplay: 'short',
    listStyle: 'narrow',
  });
};

export const startCase = <T extends string>(str: T) => R.capitalize(R.toCamelCase(str));

/*
 * Lowercase, cloud-resource-safe slug: letters/digits/hyphens only.
 * e.g. "Billy.Bob" -> "billy-bob".
 */
export const toResourceSlug = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/*
 * Like toResourceSlug but from an email's name,
 * e.g. "Billy.Bob@toro.ai" -> "billy-bob".
 */
export const emailToResourceSlug = (email: string) => toResourceSlug(email.split('@')[0]);
export const compact = <T extends unknown[]>(val: T) => filter(val, isTruthy);
export const union = <T, U>(data1: T[], data2: U[]) => unique(concat(data1, data2));

export function toInitials(name?: string | null, limit = 3) {
  if (!name) return '?';
  return (
    R.pipe(
      name,
      R.toTitleCase(),
      R.split(' '),
      R.map((word) => word.charAt(0)),
      R.filter(R.isTruthy),
      R.take(limit),
      R.join('')
    ) || '?'
  );
}

const NON_QUESTION_REACTIONS = [
  // English
  'Liked',
  'Loved',
  'Disliked',
  'Laughed at',
  'Emphasized',
  // Spanish
  'Le encantó',
  'Le gustó',
  'No le gustó',
  'Le dio risa',
  'exclamó',
  'Le encanta',
  'Le gusta',
  'No le gusta',
  'Le dia risa',
  'exclama',
];

export function shouldIgnoreAsIosReaction(text: string): boolean {
  const trimmed = text.trim();
  const pattern = new RegExp(`^(${NON_QUESTION_REACTIONS.join('|')}) “[^”]+”$`, 'i');
  return pattern.test(trimmed);
}

const REGEX_STRING_PATTERN = /^\/(.+)\/([dgimsuvy]*)$/;

/**
 * Parses a string-format regex like "/pattern/flags" into a RegExp object.
 * Returns null if the string is not in regex format.
 */
export function parseRegexString(str: string): RegExp | null {
  const parsed = REGEX_STRING_PATTERN.exec(str);
  return parsed ? new RegExp(parsed[1], parsed[2]) : null;
}

export { bullhornLink, isOpenWindowRecordEntity } from './bullhornNavigation.js';
export type { OpenWindowRecordEntity } from './bullhornNavigation.js';

export const isBullhornUrl = (url: string | null | undefined) =>
  url?.includes('bullhornstaffing.com') ?? false;

/**
 * Helper function to be used as the final `default` case in switch statements.
 * Throws an error AND results in a Typescript compilation error if there are unhandled cases for the input type, as it should always be unreachable code
 * @param value - Switch statement parameter variable.
 */
export function exhaustiveSwitchCheck(value: never): never {
  const error = new Error(`Unexpected value in exhaustive switch statement: ${value}`);
  console.error(error);
  throw error;
}

const dateFieldMap: Record<OpportunityDateFieldTarget, OpportunityDateFieldPath> = {
  created: 'createdAt',
  completed: 'data.completedDate',
};
export const prepareFiltersForGetQuery = (
  filters: OpportunityFilters
): BackendOpportunityFilters => {
  const groupedByType = entries(filters.tagFilters)
    .map(([tagType, tags]) => ({
      tagType,
      values: tags.map(({ entity }) => entity),
    }))
    .filter(({ values }) => values.length > 0);

  const dateParams = filters.dateFilter ? dateFilterToParams(filters.dateFilter) : null;

  return {
    otherFilters: filters.otherFilters,
    tagFilters: groupedByType,
    dateFilter: dateParams,
  };
};

function dateFilterToParams(filter: OpportunityDateFilter) {
  if (!filter) return null;

  const target = dateFieldMap[filter.target || 'created'];

  const range = getDateFilterRange(filter);

  return {
    startDate: range?.startDate || null,
    endDate: range?.endDate || null,
    target,
  };
}

// ------------------------------------------------------------------------------------------------------------------ //
//region Chrome extension navigation trail helpers

export function getEntityLabel(entity: TrackedEntity) {
  if (entity.name) {
    return `${entity.type} "${entity.name}" (${entity.id})`;
  }

  return `${entity.type} (${entity.id})`;
}

// Narrows a view to its tracked record, false for an untracked page or nothing open
export function isTrackedEntity(view: AtsView | null | undefined): view is TrackedEntity {
  return R.isNonNullish(view) && R.isIncludedIn(view.type, TrackedEntityType.options);
}

// getEntityLabel for any ATS view: "the Contacts list", `Lead "Test Lead"`, or "nothing open in Bullhorn"
export function getAtsViewLabel(view: AtsView) {
  if (isTrackedEntity(view)) return getEntityLabel(view);
  if (!view.kind) return 'nothing open in Bullhorn';
  if (view.kind === 'List') return view.label ? `the ${view.label} list` : 'a list';
  return view.label ? `${view.kind} "${view.label}"` : view.kind;
}

// Checks entity membership
export function isEntityTrailItem(item: NavigationTrailItem): item is EntityTrailItem {
  return R.isIncludedIn(item.type, TrackedEntityType.options);
}

// A comparable identity string for the record, url, or untracked page a trail item points at.
export const trailItemIdentity = (item: NavigationTrailItem) =>
  isEntityTrailItem(item) ? `${item.type}:${item.id}`
  : item.type === 'url' ? `url:${item.url}`
  : `atsPage:${item.kind ?? ''}:${item.label ?? ''}`;

// Verifies if two trail items are equivalent
export function isSameTrailItem(a: NavigationTrailItem, b: NavigationTrailItem): boolean {
  return trailItemIdentity(a) === trailItemIdentity(b);
}

// Same place and same visit, so repeat visits to one entity stay distinct
export function isSameTrailItemVisit(a: NavigationTrailItem, b: NavigationTrailItem): boolean {
  return isSameTrailItem(a, b) && a.visitedAt?.toMillis() === b.visitedAt?.toMillis();
}

/*
 * The trail item for where the user is right now. Off Bullhorn the tab url wins, on Bullhorn it is the
 * record or page in view. Shared by the live pill and the stored trail so the two cannot disagree
 */
export function currentTrailItem(
  context: Pick<BrowsingContext, 'tabUrl' | 'latestAtsView' | 'currentProfileSnapshotId'>,
  visitedAt: DateTime = DateTime.now()
): NavigationTrailItem {
  const { tabUrl, latestAtsView, currentProfileSnapshotId } = context;
  if (tabUrl && !isBullhornUrl(tabUrl)) return { type: 'url', url: tabUrl, visitedAt };
  if (isTrackedEntity(latestAtsView)) {
    return { ...latestAtsView, profileSnapshotId: currentProfileSnapshotId, visitedAt };
  }
  return { type: 'atsPage', ...latestAtsView, visitedAt };
}

//endregion
// ------------------------------------------------------------------------------------------------------------------ //

/*
 * Bullhorn's options API can replace non-ASCII chars in a label with a literal '?'. Treat each
 * '?' as a one-char wildcard matching only a non-ASCII char (since that's all Bullhorn corrupts),
 * and match against existingValues (the values already on the field). Returns:
 *  - the value unchanged, if it has no '?'
 *  - the single matching existing value
 *  - null, if no value matches or more than one does (caller drops it rather than guess)
 */
export function resolveCorruptedOptionValue(
  value: string,
  existingValues: readonly string[]
): string | null {
  if (!value.includes('?')) return value;
  const pattern = new RegExp(`^${value.split('?').map(RegExp.escape).join('[^\\x00-\\x7F]')}$`);
  const matches = R.unique(existingValues.filter((candidate) => pattern.test(candidate)));
  return matches.length === 1 ? matches[0] : null;
}

// A field's Bullhorn default(s) as strings — its "no selection" sentinel (scalar or array).
export function defaultValueStrings(defaultValue: unknown): string[] {
  if (R.isNullish(defaultValue)) return [];
  return (R.isArray(defaultValue) ? defaultValue : [defaultValue]).filter(R.isString);
}

// A field's "no selection" values: its Bullhorn default plus whatever the mapping configures.
export function fieldBlankEquivalents(
  defaultValue: unknown,
  configured: readonly string[] | undefined
): string[] {
  return [...defaultValueStrings(defaultValue), ...(configured ?? [])];
}

// Drop "no selection" placeholders from a value list, matching exactly or via the '?'-wildcard so corrupted variants are caught too.
export function stripBlankEquivalents<T>(
  values: readonly T[],
  blankEquivalents: readonly string[]
): T[] {
  if (!blankEquivalents.length) return [...values];
  return values.filter((val) => {
    if (!R.isString(val)) return true;
    if (blankEquivalents.includes(val)) return false;
    return !(val.includes('?') && resolveCorruptedOptionValue(val, blankEquivalents) !== null);
  });
}

// TO_MANY values are { id } by write time, so resolve each id to its option label/rawName and drop
// blank-equivalents (e.g. "Other"). Matches canonical meta options, so no '?'-corruption handling.
export function stripBlankEquivalentOptionValues<T extends { id: number }>(
  values: readonly T[],
  options: readonly { value: string | number; label?: string; rawName?: string }[] | undefined,
  blankEquivalents: readonly string[]
): T[] {
  if (!blankEquivalents.length || !options?.length) return [...values];
  const isBlank = R.isIncludedIn(blankEquivalents);
  const blankIds = new Set(
    options.filter((o) => isBlank(o.label) || isBlank(o.rawName)).map((o) => Number(o.value))
  );
  return values.filter((v) => !blankIds.has(v.id));
}

// Exact-or-subdomain match of an already-extracted hostname against bare domains (no wildcards/paths/ports).
// Both sides are case-folded here, so callers pass domains as configured. Pass a bare hostname via
// URL.hostname, not a URL (never matches) — callers own the malformed case.
export function hostMatchesDomain(hostname: string, domains: readonly string[]): boolean {
  // A fully-qualified hostname may carry a root-label dot ("foo.com.") that never appears in a configured domain.
  const host = hostname.toLowerCase().replace(/\.$/, '');
  return domains.some((rawDomain) => {
    const domain = rawDomain.toLowerCase();
    return host === domain || host.endsWith(`.${domain}`);
  });
}

// TextUs MMS image URLs (image ext or /mms_media/), never ending in punctuation or quotes so a URL closing/quoted in a sentence stays intact.
// Fresh regex per use since the global flag makes lastIndex stateful.
const MMS_IMAGE_URL_SOURCE =
  'https?:\\/\\/[^\\s<>]+?(?:\\.(?:jpg|jpeg|png|gif|webp)|\\/mms_media\\/[^\\s<>]*[^\\s<>.,;:!?)\'"])(?:\\?[^\\s<>]*[^\\s<>.,;:!?)\'"])?';
const mmsImageUrlRegex = () => new RegExp(MMS_IMAGE_URL_SOURCE, 'gi');

// Extract MMS image URLs from a message body, returning the body with the "MMS URL(s):" label and those URLs stripped.
export function extractMmsImageUrls(content?: string | null): {
  cleanedContent: string;
  imageUrls: readonly string[];
} {
  if (!content) return { cleanedContent: '', imageUrls: [] };
  const imageUrls = content.match(mmsImageUrlRegex()) ?? [];
  if (!content.includes('MMS URL(s):') && imageUrls.length === 0) {
    return { cleanedContent: content.trim(), imageUrls: [] };
  }
  const cleanedContent = content
    .replace(/MMS URL\(s\):\s*/gi, '')
    .replace(mmsImageUrlRegex(), '')
    .replace(/,\s*,/g, ',') // fix consecutive commas
    .replace(/(^,|,$)/g, '') // remove commas at start or end
    .replace(/\s{2,}/g, ' ') // collapse multiple spaces
    .trim();
  return { cleanedContent, imageUrls };
}

// Wrap bare MMS image URLs as markdown images for inline render; skips "](" targets and <autolinks> so existing markdown isn't double-wrapped.
export function wrapMmsImageUrlsAsMarkdown(content: string): string {
  return content
    .replace(/MMS URL\(s\):\s*/gi, '')
    .replace(mmsImageUrlRegex(), (match: string, offset: number, str: string) => {
      const preceding = str.slice(Math.max(0, offset - 2), offset);
      const isAlreadyMarkdown = preceding === '](' || preceding.endsWith('<');
      return isAlreadyMarkdown ? match : `![](${match})`;
    });
}
